import React, { useState, useEffect, useMemo, useRef } from "react";
import { useUnit } from "../../context/UnitContext";
import { useDevices } from "../../context/DeviceContext";
import telemetryRepository from "../../services/repositories/telemetryRepository";
import { isDemoSession } from "../../services/demoSession";
import { calculateHousingBillByLiters } from "../../utils/unitConversion";
import DailyConsumptionCard from "../../components/DailyConsumptionCard/DailyConsumptionCard";
import HourlyBreakdownChart from "../../components/HourlyBreakdownChart/HourlyBreakdownChart";
import "./WaterHistory.css";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function WaterHistory() {
  const { formatValueWithUnit } = useUnit();
  const { selectedDevice } = useDevices();

  const [mode, setMode] = useState("range"); // "range" | "month"
  const [dateRange, setDateRange] = useState(7);

  // Mes seleccionado (default: mes actual)
  const today = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // 1-12

  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [totalConsumption, setTotalConsumption] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [averageDaily, setAverageDaily] = useState(0);
  const [periodLabel, setPeriodLabel] = useState("");
  const hasSelectedInitialDay = useRef(false);
  const selectedDayKey = useRef(null);

  const userTariff = "governmental";

  // En la demo, los atajos siguen el calendario actual.
  const quickMonths = useMemo(
    () => isDemoSession()
      ? [2, 1, 0].map((offset) => {
        const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
        return {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          label: offset === 0 ? 'Este mes' : date.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' }),
        };
      })
      : [
        { year: 2025, month: 11, label: "Nov 2025" },
        { year: 2025, month: 12, label: "Dic 2025" },
        { year: today.getFullYear(), month: today.getMonth() + 1, label: "Este mes" },
      ],
    [today]
  );

  /** Historial del dispositivo seleccionado, usando el repositorio de su sesión. */
  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    hasSelectedInitialDay.current = false;

    const aplicar = (result) => {
      if (cancelado) return;
      setPeriodLabel(result.periodLabel);

      if (result.dailyData.length > 0) {
        setDailyData(result.dailyData);
        setTotalConsumption(result.summary.totalConsumption);
        setAverageDaily(result.summary.averageDaily);

        const costResult = calculateHousingBillByLiters(
          result.summary.totalConsumption,
          userTariff
        );
        setTotalCost(costResult.total);

        if (isDemoSession()) {
          selectDay(result.dailyData.find((day) => day.date === selectedDayKey.current)
            ?? result.dailyData[result.dailyData.length - 1]);
        } else if (!hasSelectedInitialDay.current) {
          selectDay(result.dailyData[0]);
          hasSelectedInitialDay.current = true;
        }
      } else {
        setDailyData([]);
        setSelectedDay(null);
        setHourlyData([]);
        setTotalConsumption(0);
        setTotalCost(0);
        setAverageDaily(0);
      }
      setLoading(false);
    };

    if (!selectedDevice?.device_id) {
      aplicar({ dailyData: [], summary: { totalConsumption: 0, averageDaily: 0 }, periodLabel: '' });
      return () => { cancelado = true; };
    }

    const m = String(selectedMonth).padStart(2, '0');
    const inicioMes = `${selectedYear}-${m}-01`;
    const finMes = `${selectedYear}-${m}-${String(new Date(selectedYear, selectedMonth, 0).getDate()).padStart(2, '0')}`;
    const opciones = mode === 'month'
      ? (isDemoSession() ? { startDate: inicioMes, endDate: finMes } : 31)
      : dateRange;
    const cargar = () => telemetryRepository
      .getAggregated(selectedDevice.device_id, opciones)
      .then(({ dailyData: dias = [] }) => {
        if (cancelado) return;
        const dailyData = dias.map((d) => {
          // "2026-08-23" con new Date() se interpreta como medianoche UTC y en
          // Mexico cae en el dia anterior. Se construye por partes.
          const [anio, mes, dia] = String(d.date).split('-').map(Number);
          return {
            date: d.date,
            dateObj: new Date(anio, mes - 1, dia),
            consumption: Number(d.consumption ?? 0),
            maxFlowRate: Number(d.maxFlowRate ?? 0),
            avgFlowRate: Number(d.avgFlowRate ?? 0),
            // hour numerico y label aparte, tal como los entrega el backend:
            // HourlyBreakdownChart busca la hora con `d.hour === i` contra
            // 0..23, y pinta el eje X (y la tabla) con `label`. Convertirlo a
            // "15:00" hacia que no encontrara ninguna y dibujara 24 ceros.
            hourlyBreakdown: (d.hourlyBreakdown ?? []).map((h) => ({
              hour: Number(h.hour),
              label: h.label ?? `${String(h.hour).padStart(2, '0')}:00`,
              consumption: Number(h.consumption ?? 0),
              avgFlowRate: Number(h.avgFlowRate ?? 0),
            })),
          };
        });
        const total = dailyData.reduce((s, d) => s + d.consumption, 0);
        aplicar({
          dailyData,
          summary: {
            totalConsumption: Number(total.toFixed(2)),
            averageDaily: dailyData.length ? Number((total / dailyData.length).toFixed(2)) : 0,
          },
          periodLabel: mode === 'month'
            ? `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`
            : `Últimos ${dateRange} días`,
        });
      })
      .catch((err) => {
        if (cancelado) return;
        console.error('No se pudo cargar el historial:', err);
        aplicar({ dailyData: [], summary: { totalConsumption: 0, averageDaily: 0 }, periodLabel: '' });
      });

    cargar();
    const interval = isDemoSession() ? setInterval(cargar, 5000) : null;
    return () => { cancelado = true; if (interval) clearInterval(interval); };
  }, [mode, dateRange, selectedYear, selectedMonth, userTariff, selectedDevice]);

  const selectDay = (day) => {
    selectedDayKey.current = day.date;
    setSelectedDay(day);
    const hourlyWithPrices = (day.hourlyBreakdown || []).map((hour) => {
      const costResult = calculateHousingBillByLiters(hour.consumption, userTariff);
      return { ...hour, cost: costResult.total };
    });
    setHourlyData(hourlyWithPrices);
  };

  const formatDate = (dateString, { withYear = true } = {}) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;

    if (dateString === todayKey) return "Hoy";
    if (dateString === yesterdayKey) return "Ayer";
    return date.toLocaleDateString("es-MX", {
      weekday: "long",
      // El año sólo donde no está ya a la vista. En la lista de días cada fila
      // lleva la fecha numérica completa debajo, así que repetirlo sólo servía
      // para que la etiqueta no cupiese y se cortara con puntos suspensivos.
      ...(withYear ? { year: "numeric" } : {}),
      month: "long",
      day: "numeric",
    });
  };

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const isFutureMonth = useMemo(() => {
    return (
      selectedYear > today.getFullYear() ||
      (selectedYear === today.getFullYear() && (isDemoSession()
        ? selectedMonth >= today.getMonth() + 1
        : selectedMonth > today.getMonth() + 1))
    );
  }, [selectedYear, selectedMonth, today]);

  const yearsAvailable = useMemo(() => {
    const years = [];
    for (let y = today.getFullYear(); y >= today.getFullYear() - 3; y--) years.push(y);
    return years;
  }, [today]);

  // Header del modo "mes" con consumo total destacado
  const showHeaderMonth = mode === "month";

  return (
    <main className="ng-history">
      <div className="ng-history__toolbar">
        <div className="ng-history__mode-tabs" role="tablist" aria-label="Modo de búsqueda">
          <button
            role="tab"
            aria-selected={mode === "range"}
            className={`ng-history__tab ${mode === "range" ? "is-active" : ""}`}
            onClick={() => setMode("range")}
          >
            <HistoryRoundedIcon fontSize="small" />
            Rango rápido
          </button>
          <button
            role="tab"
            aria-selected={mode === "month"}
            className={`ng-history__tab ${mode === "month" ? "is-active" : ""}`}
            onClick={() => setMode("month")}
          >
            <EventRoundedIcon fontSize="small" />
            Mes específico
          </button>
        </div>
      </div>

      {mode === "range" ? (
        <div className="ng-history__filters">
          <div className="ng-history__range">
            <label htmlFor="history-range">Periodo</label>
            <select
              id="history-range"
              value={dateRange}
              onChange={(e) => setDateRange(parseInt(e.target.value))}
              className="ng-history__select"
              disabled={loading}
            >
              <option value={7}>Últimos 7 días</option>
              <option value={15}>Últimos 15 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={60}>Últimos 60 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={180}>Últimos 6 meses</option>
              <option value={365}>Último año</option>
            </select>
            {loading && <span className="loading-indicator">Cargando…</span>}
          </div>

          <div className="ng-history__quick">
            <span className="ng-history__quick-label">Atajos:</span>
            {quickMonths.map((qm) => (
              <button
                key={`${qm.year}-${qm.month}`}
                className="ng-history__chip"
                onClick={() => {
                  setMode("month");
                  setSelectedYear(qm.year);
                  setSelectedMonth(qm.month);
                }}
              >
                {qm.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="ng-history__filters">
          <div className="ng-history__month-picker">
            <button
              className="ng-history__nav-btn"
              onClick={goToPreviousMonth}
              aria-label="Mes anterior"
              disabled={loading}
            >
              <ChevronLeftRoundedIcon />
            </button>

            <div className="ng-history__month-selects">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="ng-history__select"
                disabled={loading}
                aria-label="Mes"
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={name} value={idx + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="ng-history__select"
                disabled={loading}
                aria-label="Año"
              >
                {yearsAvailable.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="ng-history__nav-btn"
              onClick={goToNextMonth}
              aria-label="Mes siguiente"
              disabled={loading || isFutureMonth}
            >
              <ChevronRightRoundedIcon />
            </button>

            {loading && <span className="loading-indicator">Cargando…</span>}
          </div>

          <div className="ng-history__quick">
            <span className="ng-history__quick-label">Saltos rápidos:</span>
            {quickMonths.map((qm) => {
              const active = qm.year === selectedYear && qm.month === selectedMonth;
              return (
                <button
                  key={`${qm.year}-${qm.month}`}
                  className={`ng-history__chip ${active ? "is-active" : ""}`}
                  onClick={() => {
                    setSelectedYear(qm.year);
                    setSelectedMonth(qm.month);
                  }}
                >
                  {qm.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showHeaderMonth && (
        <div className="ng-history__period-banner">
          <CalendarMonthRoundedIcon className="ng-history__period-icon" />
          <div>
            <span className="ng-history__period-title">{periodLabel}</span>
            <span className="ng-history__period-sub">
              {dailyData.length} día{dailyData.length === 1 ? "" : "s"} con datos
            </span>
          </div>
        </div>
      )}

      <div className="history-stats">
        <div className="stat-card">
          <CalendarMonthRoundedIcon className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Días con datos</span>
            <span className="stat-value">{dailyData.length}</span>
          </div>
        </div>
        <div className="stat-card">
          <WaterDropRoundedIcon className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Consumo total</span>
            <span className="stat-value">{formatValueWithUnit(totalConsumption)}</span>
          </div>
        </div>
        <div className="stat-card">
          <AttachMoneyRoundedIcon
            className="stat-icon"
            style={{
              color: "var(--success-on-soft)",
              background: "var(--success-soft)",
              borderColor: "var(--success-border)",
            }}
          />
          <div className="stat-content">
            <span className="stat-label">Costo estimado</span>
            <span className="stat-value" style={{ color: "var(--success-on-soft)" }}>
              ${totalCost.toFixed(2)} MXN
            </span>
          </div>
        </div>
        <div className="stat-card">
          <TrendingUpRoundedIcon className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Promedio diario</span>
            <span className="stat-value">{formatValueWithUnit(averageDaily)}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p className="subtle">Cargando historial…</p>
        </div>
      ) : (
        <div className="history-content">
          <div className="daily-list-container">
            <h4 className="section-title">Consumo por día · {periodLabel}</h4>
            {dailyData.length === 0 ? (
              <div className="no-data-message">
                <WaterDropRoundedIcon style={{ fontSize: 48, opacity: 0.3 }} />
                <p>No hay datos de consumo para este período</p>
                {mode === "month" && (
                  <p className="no-data-hint">
                    Tip: prueba con otro mes o cambia a "Rango rápido".
                  </p>
                )}
              </div>
            ) : (
              <div className="daily-list">
                {dailyData.map((day) => (
                  <DailyConsumptionCard
                    key={day.date}
                    date={day.date}
                    dateLabel={formatDate(day.date, { withYear: false })}
                    consumption={day.consumption}
                    maxFlowRate={day.maxFlowRate}
                    avgFlowRate={day.avgFlowRate}
                    isSelected={selectedDay?.date === day.date}
                    onClick={() => selectDay(day)}
                  />
                ))}
              </div>
            )}
          </div>

          {selectedDay && (
            <div className="hourly-breakdown-container">
              <h4 className="section-title">
                Desglose por hora · {formatDate(selectedDay.date)}
              </h4>
              <HourlyBreakdownChart
                data={hourlyData}
                title="Consumo de agua por hora (Litros)"
                hourlyLimit={isDemoSession() ? selectedDevice?.configuration?.max_daily_usage / 24 : undefined}
              />

              <div className="hourly-table-container">
                <table className="hourly-table">
                  <thead>
                    <tr>
                      <th>Hora</th>
                      <th>Consumo (L)</th>
                      <th>Costo (MXN)</th>
                      <th>Flujo prom. (L/min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hourlyData.map((hour) => (
                      <tr key={hour.hour}>
                        <td>{hour.label}</td>
                        <td>
                          <strong>{hour.consumption.toFixed(2)}</strong>
                        </td>
                        <td
                          style={{
                            color: hour.cost > 0 ? "var(--success-color)" : "var(--text-muted)",
                          }}
                        >
                          ${(hour.cost || 0).toFixed(2)}
                        </td>
                        <td>{hour.avgFlowRate.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total del día</td>
                      <td>{selectedDay?.consumption?.toFixed(2) || 0} L</td>
                      <td style={{ color: "var(--success-on-soft)" }}>
                        $
                        {calculateHousingBillByLiters(
                          selectedDay?.consumption || 0,
                          userTariff
                        ).total.toFixed(2)}
                      </td>
                      <td>{selectedDay?.avgFlowRate?.toFixed(2) || 0}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}

export default WaterHistory;
